import type {
	API_AddSupportTicketCommentRequest,
	API_AddSupportTicketCommentResponse,
	API_CancelSupportTicketResponse,
	API_CreateSupportTicketRequest,
	API_CreateSupportTicketResponse,
	API_GetSupportTicketResponse,
	API_ListSupportTicketsQuery,
	API_ListSupportTicketsResponse,
	API_ReopenSupportTicketResponse,
	API_ResolveSupportTicketResponse,
	SupportTicketStatus,
	SupportTicketType,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const createSupportTicket = async (
	token: string,
	data: API_CreateSupportTicketRequest,
): Promise<API_CreateSupportTicketResponse> => {
	const response = await api.post("/support/tickets", data, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

export const getSupportTicket = async (
	token: string,
	ticketId: string,
): Promise<API_GetSupportTicketResponse> => {
	const response = await api.get(`/support/tickets/${ticketId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

export const listSupportTickets = async (
	token: string,
	query: API_ListSupportTicketsQuery,
): Promise<API_ListSupportTicketsResponse> => {
	const response = await api.get("/support/tickets", {
		headers: { Authorization: `Bearer ${token}` },
		params: query,
	});
	return response.data;
};

export const addSupportTicketComment = async (
	token: string,
	ticketId: string,
	data: API_AddSupportTicketCommentRequest,
): Promise<API_AddSupportTicketCommentResponse> => {
	const response = await api.post(
		`/support/tickets/${ticketId}/comments`,
		data,
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
	return response.data;
};

export const cancelSupportTicket = async (
	token: string,
	ticketId: string,
): Promise<API_CancelSupportTicketResponse> => {
	const response = await api.patch(
		`/support/tickets/${ticketId}/cancel`,
		{},
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
	return response.data;
};

export const resolveSupportTicket = async (
	token: string,
	ticketId: string,
): Promise<API_ResolveSupportTicketResponse> => {
	const response = await api.patch(
		`/support/tickets/${ticketId}/resolve`,
		{},
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
	return response.data;
};

export const reopenSupportTicket = async (
	token: string,
	ticketId: string,
): Promise<API_ReopenSupportTicketResponse> => {
	const response = await api.patch(
		`/support/tickets/${ticketId}/reopen`,
		{},
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
	return response.data;
};

export const fetchUserSupportTicketByIdQueryOptions = (
	token: string,
	ticketId: string,
) =>
	queryOptions({
		queryKey: ["get_support_ticket", ticketId],
		queryFn: () => getSupportTicket(token, ticketId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchUserSupportTicketsQueryOptions = (
	userId: string,
	token: string,
	query: API_ListSupportTicketsQuery,
) =>
	queryOptions({
		queryKey: ["list_support_tickets", userId, query],
		queryFn: () => listSupportTickets(token, query),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateSupportTicket = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			data,
		}: {
			token: string;
			data: API_CreateSupportTicketRequest;
		}) => createSupportTicket(token, data),
		toastMessages: {
			loading: t("help.supportSubmitting"),
			success: t("help.supportSubmitSuccess"),
			error: t("help.supportSubmitError"),
		},
		onSuccess: () => {
			// Invalidate support tickets list
			queryClient.invalidateQueries({
				queryKey: ["list_support_tickets"],
			});
		},
	});
};

export const useAddSupportTicketComment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			ticketId,
			data,
		}: {
			token: string;
			ticketId: string;
			data: API_AddSupportTicketCommentRequest;
		}) => addSupportTicketComment(token, ticketId, data),
		toastMessages: {
			loading: t("help.commentSubmitting"),
			success: t("help.commentSubmitSuccess"),
			error: t("help.commentSubmitError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate specific ticket to refresh comments
			queryClient.invalidateQueries({
				queryKey: ["get_support_ticket", variables.ticketId],
			});
			// Also invalidate the list
			queryClient.invalidateQueries({
				queryKey: ["list_support_tickets"],
			});
		},
	});
};

export const useCancelSupportTicket = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({ token, ticketId }: { token: string; ticketId: string }) =>
			cancelSupportTicket(token, ticketId),
		toastMessages: {
			loading: t("help.ticketCanceling"),
			success: t("help.ticketCancelSuccess"),
			error: t("help.ticketCancelError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["get_support_ticket", variables.ticketId],
			});
			queryClient.invalidateQueries({
				queryKey: ["list_support_tickets"],
			});
		},
	});
};

export const useResolveSupportTicket = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({ token, ticketId }: { token: string; ticketId: string }) =>
			resolveSupportTicket(token, ticketId),
		toastMessages: {
			loading: t("help.ticketResolving"),
			success: t("help.ticketResolveSuccess"),
			error: t("help.ticketResolveError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["get_support_ticket", variables.ticketId],
			});
			queryClient.invalidateQueries({
				queryKey: ["list_support_tickets"],
			});
		},
	});
};

export const useReopenSupportTicket = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({ token, ticketId }: { token: string; ticketId: string }) =>
			reopenSupportTicket(token, ticketId),
		toastMessages: {
			loading: t("help.ticketReopening"),
			success: t("help.ticketReopenSuccess"),
			error: t("help.ticketReopenError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["get_support_ticket", variables.ticketId],
			});
			queryClient.invalidateQueries({
				queryKey: ["list_support_tickets"],
			});
		},
	});
};

// Helper functions for UI
export const getSupportTicketStatusLabel = (
	status: SupportTicketStatus,
): string => {
	const statusLabels: Record<SupportTicketStatus, string> = {
		open: "Open",
		waiting_for_user: "Waiting for User",
		waiting_for_staff: "Waiting for Staff",
		resolved: "Resolved",
		closed: "Closed",
	};
	return statusLabels[status];
};

export const getSupportTicketTypeLabel = (type: SupportTicketType): string => {
	const typeLabels: Record<SupportTicketType, string> = {
		suggestion: "Suggestion",
		request: "Request",
		complaint: "Complaint",
	};
	return typeLabels[type];
};

export const getSupportTicketStatusColor = (
	status: SupportTicketStatus,
): string => {
	const statusColors: Record<SupportTicketStatus, string> = {
		open: "bg-blue-100 text-blue-800",
		waiting_for_user: "bg-yellow-100 text-yellow-800",
		waiting_for_staff: "bg-orange-100 text-orange-800",
		resolved: "bg-green-100 text-green-800",
		closed: "bg-gray-100 text-gray-800",
	};
	return statusColors[status];
};

export const getSupportTicketTypeColor = (type: SupportTicketType): string => {
	const typeColors: Record<SupportTicketType, string> = {
		suggestion: "bg-purple-100 text-purple-800",
		request: "bg-blue-100 text-blue-800",
		complaint: "bg-red-100 text-red-800",
	};
	return typeColors[type];
};
